import { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import axios from 'axios';
import { FileBarChart, Download, Users, Check, Clock, AlertTriangle, Filter, X, FileText, Building, TrendingUp, Printer } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { LOGO_SIMBOLO } from '../../assets/logos';

const API = `${window.location.origin}/api`;

const FinancialReports = () => {
  const { token } = useAdminAuth();
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [observations, setObservations] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, contractsRes] = await Promise.all([
        axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/contracts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setClients(clientsRes.data);
      setContracts(contractsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const search = clientSearch.toLowerCase();
    const matchesSearch = client.full_name?.toLowerCase().includes(search) ||
                         client.email?.toLowerCase().includes(search) ||
                         client.company?.toLowerCase().includes(search);
    const notSelected = !selectedClients.find(c => c.email === client.email);
    return matchesSearch && notSelected;
  }).slice(0, 10);

  const handleSelectClient = (client) => {
    setSelectedClients([...selectedClients, client]);
    setClientSearch('');
    setShowClientDropdown(false);
  };

  const handleRemoveClient = (email) => {
    setSelectedClients(selectedClients.filter(c => c.email !== email));
  };

  const getInstallmentStatus = (contract, index) => {
    const payments = contract.installment_payments || [];
    const storedStatus = payments[index] || 'pending';
    
    if (storedStatus === 'paid') return 'paid';
    if (storedStatus === 'cancelled') return 'cancelled';
    
    // If contract is cancelled, unpaid installments are cancelled
    if (contract.status === 'cancelled') return 'cancelled';
    
    const dueDate = contract.due_dates?.[index];
    if (dueDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const parts = dueDate.split('-');
      const dueDateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      dueDateObj.setHours(0, 0, 0, 0);
      if (today > dueDateObj) return 'overdue';
    }
    return storedStatus;
  };

  const formatDateBR = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const getStructuredReportData = () => {
    let filteredContracts = contracts;
    if (selectedClients.length > 0) {
      const selectedEmails = selectedClients.map(c => c.email);
      filteredContracts = contracts.filter(c => selectedEmails.includes(c.client_email));
    }

    const companiesMap = new Map();
    
    filteredContracts.forEach(contract => {
      const client = clients.find(c => c.email === contract.client_email);
      const companyName = client?.company || 'Pessoa Física';
      
      if (!companiesMap.has(companyName)) {
        companiesMap.set(companyName, {
          name: companyName,
          clients: new Map(),
          totals: { total: 0, paid: 0, pending: 0, overdue: 0, cancelled: 0, totalValue: 0, paidValue: 0, pendingValue: 0, overdueValue: 0, cancelledValue: 0 }
        });
      }
      
      const company = companiesMap.get(companyName);
      const clientKey = contract.client_email;
      
      if (!company.clients.has(clientKey)) {
        company.clients.set(clientKey, {
          name: client?.full_name || contract.client_email,
          email: contract.client_email,
          phone: client?.phone || '',
          contracts: []
        });
      }
      
      const clientData = company.clients.get(clientKey);
      const contractInstallments = [];
      
      contract.due_dates?.forEach((dueDate, index) => {
        const status = getInstallmentStatus(contract, index);
        if (statusFilter !== 'all' && status !== statusFilter) return;
        if (dateFrom && dueDate < dateFrom) return;
        if (dateTo && dueDate > dateTo) return;
        
        const value = contract.installment_value || 0;
        contractInstallments.push({
          number: index + 1,
          total: contract.installments,
          dueDate,
          value,
          status
        });
        
        company.totals.total++;
        company.totals.totalValue += value;
        company.totals[status]++;
        company.totals[`${status}Value`] += value;
      });
      
      if (contractInstallments.length > 0) {
        clientData.contracts.push({
          id: contract.id,
          description: contract.description || `Contrato ${contract.id.substring(0, 8)}`,
          totalValue: contract.total_value,
          status: contract.status,
          installments: contractInstallments
        });
      }
    });

    return Array.from(companiesMap.values()).filter(c => c.totals.total > 0);
  };

  const getGlobalSummary = (companies) => {
    return companies.reduce((acc, company) => ({
      total: acc.total + company.totals.total,
      paid: acc.paid + company.totals.paid,
      pending: acc.pending + company.totals.pending,
      overdue: acc.overdue + company.totals.overdue,
      cancelled: acc.cancelled + company.totals.cancelled,
      totalValue: acc.totalValue + company.totals.totalValue,
      paidValue: acc.paidValue + company.totals.paidValue,
      pendingValue: acc.pendingValue + company.totals.pendingValue,
      overdueValue: acc.overdueValue + company.totals.overdueValue,
      cancelledValue: acc.cancelledValue + company.totals.cancelledValue
    }), { total: 0, paid: 0, pending: 0, overdue: 0, cancelled: 0, totalValue: 0, paidValue: 0, pendingValue: 0, overdueValue: 0, cancelledValue: 0 });
  };

  // Draw donut chart in PDF
  const drawDonutChart = (doc, centerX, centerY, outerRadius, innerRadius, data, colors) => {
    const total = data.reduce((sum, d) => sum + d, 0);
    if (total === 0) return;
    
    let startAngle = -Math.PI / 2;
    
    data.forEach((value, index) => {
      if (value === 0) return;
      const sliceAngle = (value / total) * 2 * Math.PI;
      const endAngle = startAngle + sliceAngle;
      
      // Draw arc segments using small lines
      doc.setFillColor(...colors[index]);
      const steps = Math.max(Math.floor(sliceAngle * 30), 10);
      
      for (let i = 0; i < steps; i++) {
        const angle1 = startAngle + (sliceAngle * i / steps);
        const angle2 = startAngle + (sliceAngle * (i + 1) / steps);
        
        const x1 = centerX + outerRadius * Math.cos(angle1);
        const y1 = centerY + outerRadius * Math.sin(angle1);
        const x2 = centerX + outerRadius * Math.cos(angle2);
        const y2 = centerY + outerRadius * Math.sin(angle2);
        const x3 = centerX + innerRadius * Math.cos(angle2);
        const y3 = centerY + innerRadius * Math.sin(angle2);
        const x4 = centerX + innerRadius * Math.cos(angle1);
        const y4 = centerY + innerRadius * Math.sin(angle1);
        
        // Draw quadrilateral
        doc.triangle(x1, y1, x2, y2, x3, y3, 'F');
        doc.triangle(x1, y1, x3, y3, x4, y4, 'F');
      }
      
      startAngle = endAngle;
    });
  };

  const generatePDF = async () => {
    setGenerating(true);
    
    try {
      const companies = getStructuredReportData();
      const summary = getGlobalSummary(companies);
      
      const doc = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 18;
      let yPos = margin;

      const checkPageBreak = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - 25) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // ========== MINIMAL HEADER ==========
      // Title
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Relatório Financeiro', margin, yPos + 5);
      
      // Subtitle with date
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      const reportDate = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
      doc.text(reportDate, margin, yPos + 12);
      
      // Thin accent line
      doc.setDrawColor(0, 74, 173);
      doc.setLineWidth(0.8);
      doc.line(margin, yPos + 17, margin + 40, yPos + 17);
      
      yPos += 25;

      // ========== FILTERS ROW ==========
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      
      let filterLine = '';
      if (selectedClients.length > 0) {
        filterLine += `Clientes: ${selectedClients.map(c => c.full_name).join(', ').substring(0, 50)}`;
        if (selectedClients.length > 2) filterLine += '...';
      } else {
        filterLine += 'Todos os clientes';
      }
      filterLine += '  •  ';
      filterLine += statusFilter === 'all' ? 'Todos os status' : `Status: ${statusFilter === 'paid' ? 'Pagas' : statusFilter === 'pending' ? 'Pendentes' : statusFilter === 'overdue' ? 'Em Atraso' : 'Canceladas'}`;
      if (dateFrom || dateTo) {
        filterLine += `  •  Período: ${dateFrom ? formatDateBR(dateFrom) : '...'} a ${dateTo ? formatDateBR(dateTo) : '...'}`;
      }
      
      doc.text(filterLine, margin, yPos);
      yPos += 8;

      // ========== SUMMARY METRICS ==========
      const metricWidth = (pageWidth - 2 * margin) / 5;
      
      const metrics = [
        { label: 'Total', value: summary.total, amount: summary.totalValue, color: [80, 80, 80] },
        { label: 'Pagas', value: summary.paid, amount: summary.paidValue, color: [34, 139, 34] },
        { label: 'Pendentes', value: summary.pending, amount: summary.pendingValue, color: [200, 150, 0] },
        { label: 'Em Atraso', value: summary.overdue, amount: summary.overdueValue, color: [200, 50, 50] },
        { label: 'Canceladas', value: summary.cancelled, amount: summary.cancelledValue, color: [150, 150, 150] }
      ];

      metrics.forEach((metric, idx) => {
        const x = margin + idx * metricWidth;
        
        // Label
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(130, 130, 130);
        doc.text(metric.label.toUpperCase(), x, yPos);
        
        // Value
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...metric.color);
        doc.text(String(metric.value), x, yPos + 8);
        
        // Amount
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(formatCurrency(metric.amount), x, yPos + 14);
      });

      yPos += 22;

      // ========== PROGRESS BAR ==========
      const barHeight = 4;
      const barWidth = pageWidth - 2 * margin;
      
      // Background
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(margin, yPos, barWidth, barHeight, 1, 1, 'F');
      
      if (summary.total > 0) {
        let barX = margin;
        
        // Paid
        const paidW = (summary.paid / summary.total) * barWidth;
        if (paidW > 0) {
          doc.setFillColor(34, 139, 34);
          doc.roundedRect(barX, yPos, Math.max(paidW, 2), barHeight, 1, 0, 'F');
          barX += paidW;
        }
        
        // Pending
        const pendingW = (summary.pending / summary.total) * barWidth;
        if (pendingW > 0) {
          doc.setFillColor(220, 180, 0);
          doc.rect(barX, yPos, pendingW, barHeight, 'F');
          barX += pendingW;
        }
        
        // Overdue
        const overdueW = (summary.overdue / summary.total) * barWidth;
        if (overdueW > 0) {
          doc.setFillColor(200, 50, 50);
          doc.roundedRect(barX, yPos, overdueW, barHeight, 0, 1, 'F');
        }
      }

      yPos += 12;

      // ========== SEPARATOR ==========
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.3);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      // ========== COMPANIES SECTION ==========
      companies.forEach((company) => {
        checkPageBreak(40);

        // Company name
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text(company.name, margin, yPos);
        
        // Company stats (right aligned)
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        const companyStats = `${company.totals.total} parcelas  ·  ${formatCurrency(company.totals.totalValue)}`;
        doc.text(companyStats, pageWidth - margin, yPos, { align: 'right' });
        
        yPos += 10;

        // Clients
        Array.from(company.clients.values()).forEach((client) => {
          checkPageBreak(35);

          // Client name on its own line
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(60, 60, 60);
          doc.text(client.name, margin + 3, yPos);
          
          // Email on same line but with proper spacing
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          doc.setTextColor(140, 140, 140);
          // Reset to bold font to get correct width, then switch back
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          const nameWidth = doc.getTextWidth(client.name);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          doc.text(`  ·  ${client.email}`, margin + 3 + nameWidth, yPos);
          
          yPos += 8;

          // Contracts
          client.contracts.forEach((contract) => {
            checkPageBreak(35);

            // Contract description
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            const contractLabel = contract.description.length > 50 
              ? contract.description.substring(0, 50) + '...' 
              : contract.description;
            doc.text(`${contractLabel}  ·  ${formatCurrency(contract.totalValue)}`, margin + 6, yPos);
            yPos += 5;

            // Installments table
            const tableBody = contract.installments.map(inst => [
              `${inst.number}/${inst.total}`,
              formatDateBR(inst.dueDate),
              formatCurrency(inst.value),
              inst.status === 'paid' ? 'Pago' : inst.status === 'overdue' ? 'Atraso' : inst.status === 'cancelled' ? 'Cancelada' : 'Pendente'
            ]);

            autoTable(doc, {
              startY: yPos,
              head: [['Parcela', 'Vencimento', 'Valor', 'Status']],
              body: tableBody,
              theme: 'plain',
              styles: {
                fontSize: 7,
                cellPadding: 1.5,
                lineColor: [240, 240, 240],
                lineWidth: 0.1
              },
              headStyles: {
                fillColor: [250, 250, 250],
                textColor: [100, 100, 100],
                fontStyle: 'bold',
                fontSize: 6
              },
              columnStyles: {
                0: { cellWidth: 20, halign: 'center' },
                1: { cellWidth: 28, halign: 'center' },
                2: { cellWidth: 30, halign: 'right' },
                3: { cellWidth: 20, halign: 'center' }
              },
              didParseCell: (data) => {
                if (data.column.index === 3 && data.section === 'body') {
                  const text = data.cell.raw;
                  if (text === 'Pago') {
                    data.cell.styles.textColor = [34, 139, 34];
                  } else if (text === 'Atraso') {
                    data.cell.styles.textColor = [200, 50, 50];
                  } else if (text === 'Cancelada') {
                    data.cell.styles.textColor = [150, 150, 150];
                  } else {
                    data.cell.styles.textColor = [180, 140, 0];
                  }
                  data.cell.styles.fontStyle = 'bold';
                }
              },
              margin: { left: margin + 6, right: margin },
              tableWidth: pageWidth - 2 * margin - 6
            });

            yPos = doc.lastAutoTable.finalY + 5;
          });
          
          yPos += 3;
        });

        // Company subtotal
        checkPageBreak(12);
        
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        const subtotalLine = `${company.totals.paid} pagas  ·  ${company.totals.pending} pendentes  ·  ${company.totals.overdue} em atraso` + (company.totals.cancelled > 0 ? `  ·  ${company.totals.cancelled} canceladas` : '');
        doc.text(subtotalLine, margin + 3, yPos);
        
        yPos += 8;
        
        // Separator
        doc.setDrawColor(240, 240, 240);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;
      });

      // ========== GRAND TOTAL ==========
      checkPageBreak(20);
      
      doc.setFillColor(248, 248, 248);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 14, 2, 2, 'F');
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(50, 50, 50);
      doc.text('TOTAL', margin + 5, yPos + 9);
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`${summary.total} parcelas`, margin + 25, yPos + 9);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(formatCurrency(summary.totalValue), pageWidth - margin - 5, yPos + 9, { align: 'right' });

      yPos += 20;

      // ========== OBSERVATIONS SECTION ==========
      if (observations && observations.trim()) {
        checkPageBreak(30);
        
        // Section title
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 50, 50);
        doc.text('Observações', margin, yPos);
        yPos += 6;
        
        // Observations box
        doc.setFillColor(255, 252, 240);
        doc.setDrawColor(230, 220, 180);
        doc.setLineWidth(0.3);
        
        // Calculate text height
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const maxWidth = pageWidth - 2 * margin - 10;
        const lines = doc.splitTextToSize(observations.trim(), maxWidth);
        const textHeight = lines.length * 5 + 8;
        
        // Check if we need a new page for the entire observations box
        if (yPos + textHeight > pageHeight - 25) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.roundedRect(margin, yPos, pageWidth - 2 * margin, textHeight, 2, 2, 'FD');
        
        // Observations text
        doc.setTextColor(80, 70, 50);
        doc.text(lines, margin + 5, yPos + 6);
        
        yPos += textHeight + 5;
      }

      // ========== FOOTER ON ALL PAGES ==========
      const pageCount = doc.internal.getNumberOfPages();
      
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Footer line
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.2);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
        
        // Small logo symbol
        try {
          doc.addImage(LOGO_SIMBOLO, 'PNG', margin, pageHeight - 13, 8, 8);
        } catch (e) {}
        
        // Company info
        doc.setTextColor(140, 140, 140);
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.text('Arcano Projetos de Tecnologia', margin + 11, pageHeight - 10);
        doc.text('www.arcanoprojetos.com', margin + 11, pageHeight - 6);
        
        // Page number
        doc.text(`${i} / ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
      }

      // Open PDF in new tab instead of downloading
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Erro ao gerar PDF: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const companies = getStructuredReportData();
  const summary = getGlobalSummary(companies);

  const getStatusBadge = (status) => {
    const configs = {
      paid: { label: 'Pago', bg: 'bg-green-100', text: 'text-green-700', icon: Check },
      pending: { label: 'Pendente', bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      overdue: { label: 'Em Atraso', bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle },
      cancelled: { label: 'Cancelada', bg: 'bg-gray-100', text: 'text-gray-400', icon: AlertTriangle }
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileBarChart className="text-arcano-blue" size={28} />
            Relatórios Financeiros
          </h1>
          <p className="text-gray-600 text-sm md:text-base">Gere relatórios profissionais de parcelas por cliente ou empresa</p>
        </div>
        <button
          onClick={generatePDF}
          disabled={generating || summary.total === 0}
          className="flex items-center gap-2 bg-arcano-blue text-white px-5 py-2.5 rounded-lg hover:bg-arcano-blue-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          data-testid="generate-pdf-btn"
        >
          {generating ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
          ) : (
            <Printer size={18} />
          )}
          <span className="font-medium">Gerar PDF</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Filter size={18} className="text-arcano-blue" />
          Filtros do Relatório
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Client Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Selecionar Clientes</label>
            <div className="relative">
              <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={16} />
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(e.target.value.length > 0); }}
                onFocus={() => clientSearch.length > 0 && setShowClientDropdown(true)}
                placeholder="Buscar por nome, email ou empresa..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-arcano-blue/20 focus:border-arcano-blue transition-all"
              />
              
              {showClientDropdown && filteredClients.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredClients.map((client) => (
                    <button key={client.email} type="button" onClick={() => handleSelectClient(client)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors">
                      <div className="font-medium text-gray-900 text-sm">{client.full_name}</div>
                      <div className="text-xs text-gray-500">{client.email} {client.company && `• ${client.company}`}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {selectedClients.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selectedClients.map(client => (
                  <span key={client.email} className="inline-flex items-center gap-1 px-2.5 py-1 bg-arcano-blue/10 text-arcano-blue rounded-full text-xs font-medium">
                    {client.full_name}
                    <button onClick={() => handleRemoveClient(client.email)} className="hover:bg-arcano-blue/20 rounded-full p-0.5 transition-colors">
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <button onClick={() => setSelectedClients([])} className="text-xs text-gray-500 hover:text-gray-700 ml-1">Limpar</button>
              </div>
            )}
            {selectedClients.length === 0 && <p className="text-xs text-gray-500 mt-1">Deixe em branco para todos</p>}
          </div>
          
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-arcano-blue/20 focus:border-arcano-blue transition-all">
              <option value="all">Todos</option>
              <option value="paid">Pagas</option>
              <option value="pending">Pendentes</option>
              <option value="overdue">Em Atraso</option>
              <option value="cancelled">Canceladas</option>
            </select>
          </div>
          
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Período</label>
            <div className="flex gap-2">
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-arcano-blue/20 focus:border-arcano-blue transition-all" />
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-arcano-blue/20 focus:border-arcano-blue transition-all" />
            </div>
          </div>
        </div>

        {/* Observations Field */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Observações
            <span className="text-gray-400 font-normal ml-1">(aparecerá no final do PDF)</span>
          </label>
          <textarea
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            placeholder="Digite observações adicionais para incluir no relatório..."
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-arcano-blue/20 focus:border-arcano-blue transition-all resize-none"
            data-testid="observations-input"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-2 ${summary.cancelled > 0 ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3`}>
        {[
          { label: 'Total', qty: summary.total, value: summary.totalValue, color: 'gray', icon: FileText },
          { label: 'Pagas', qty: summary.paid, value: summary.paidValue, color: 'green', icon: Check },
          { label: 'Pendentes', qty: summary.pending, value: summary.pendingValue, color: 'yellow', icon: Clock },
          { label: 'Em Atraso', qty: summary.overdue, value: summary.overdueValue, color: 'red', icon: AlertTriangle },
          ...(summary.cancelled > 0 ? [{ label: 'Canceladas', qty: summary.cancelled, value: summary.cancelledValue, color: 'slate', icon: AlertTriangle }] : [])
        ].map((card, idx) => {
          const Icon = card.icon;
          const colorClasses = {
            gray: 'border-gray-300 bg-gray-50',
            green: 'border-green-300 bg-green-50',
            yellow: 'border-yellow-300 bg-yellow-50',
            red: 'border-red-300 bg-red-50',
            slate: 'border-gray-200 bg-gray-50'
          };
          const textClasses = {
            gray: 'text-gray-700',
            green: 'text-green-700',
            yellow: 'text-yellow-700',
            red: 'text-red-700',
            slate: 'text-gray-400'
          };
          return (
            <div key={idx} className={`rounded-xl border-2 p-4 ${colorClasses[card.color]}`}>
              <div className={`flex items-center gap-2 mb-2 ${textClasses[card.color]}`}>
                <Icon size={16} />
                <span className="text-sm font-medium">{card.label}</span>
              </div>
              <p className={`text-2xl font-bold ${textClasses[card.color]}`}>{card.qty}</p>
              <p className={`text-sm ${textClasses[card.color]} opacity-80`}>{formatCurrency(card.value)}</p>
            </div>
          );
        })}
      </div>

      {/* Progress Bar */}
      {summary.total > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <TrendingUp size={16} className="text-arcano-blue" />
              Distribuição
            </h3>
            <div className="flex gap-4 text-xs">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-green-500 rounded-full"></span> {Math.round((summary.paid / summary.total) * 100)}%</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-yellow-500 rounded-full"></span> {Math.round((summary.pending / summary.total) * 100)}%</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-500 rounded-full"></span> {Math.round((summary.overdue / summary.total) * 100)}%</span>
              {summary.cancelled > 0 && <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-gray-400 rounded-full"></span> {Math.round((summary.cancelled / summary.total) * 100)}%</span>}
            </div>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden flex">
            <div className="bg-green-500 transition-all duration-500" style={{ width: `${(summary.paid / summary.total) * 100}%` }}></div>
            <div className="bg-yellow-500 transition-all duration-500" style={{ width: `${(summary.pending / summary.total) * 100}%` }}></div>
            <div className="bg-red-500 transition-all duration-500" style={{ width: `${(summary.overdue / summary.total) * 100}%` }}></div>
            {summary.cancelled > 0 && <div className="bg-gray-400 transition-all duration-500" style={{ width: `${(summary.cancelled / summary.total) * 100}%` }}></div>}
          </div>
        </div>
      )}

      {/* Preview by Company */}
      <div className="space-y-4">
        {companies.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FileBarChart size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Nenhuma parcela encontrada</p>
          </div>
        ) : (
          companies.map((company, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Company Header */}
              <div className="bg-gradient-to-r from-arcano-blue to-blue-600 text-white p-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <Building size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold">{company.name}</h3>
                      <p className="text-blue-100 text-sm">{company.clients.size} cliente(s) • {company.totals.total} parcelas</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold">{formatCurrency(company.totals.totalValue)}</p>
                  </div>
                </div>
              </div>

              {/* Clients */}
              <div className="divide-y divide-gray-100">
                {Array.from(company.clients.values()).map((client, clientIdx) => (
                  <div key={clientIdx} className="p-4">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                      <Users size={14} className="text-gray-400" />
                      <span className="font-semibold text-gray-900 text-sm">{client.name}</span>
                      <span className="text-gray-400 text-xs">({client.email})</span>
                    </div>
                    
                    {client.contracts.map((contract, contractIdx) => (
                      <div key={contractIdx} className="ml-4 mb-3 last:mb-0">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText size={12} className="text-arcano-blue" />
                          <span className="text-sm font-medium text-gray-700">{contract.description}</span>
                          <span className="text-xs text-gray-400">({formatCurrency(contract.totalValue)})</span>
                        </div>
                        
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Parcela</th>
                                <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Vencimento</th>
                                <th className="px-2 py-1.5 text-right font-semibold text-gray-600">Valor</th>
                                <th className="px-2 py-1.5 text-center font-semibold text-gray-600">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {contract.installments.slice(0, 5).map((inst, instIdx) => (
                                <tr key={instIdx}>
                                  <td className="px-2 py-1.5">{inst.number}/{inst.total}</td>
                                  <td className="px-2 py-1.5">{formatDateBR(inst.dueDate)}</td>
                                  <td className="px-2 py-1.5 text-right font-medium">{formatCurrency(inst.value)}</td>
                                  <td className="px-2 py-1.5 text-center">{getStatusBadge(inst.status)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {contract.installments.length > 5 && (
                            <p className="text-xs text-gray-400 mt-1 text-center">+{contract.installments.length - 5} no PDF</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Company Footer */}
              <div className="bg-gray-50 px-4 py-2 flex items-center justify-between text-xs border-t">
                <div className="flex gap-3">
                  <span className="text-green-600 font-medium">{company.totals.paid} pagas</span>
                  <span className="text-yellow-600 font-medium">{company.totals.pending} pendentes</span>
                  <span className="text-red-600 font-medium">{company.totals.overdue} em atraso</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FinancialReports;
